"use client";

import * as RadixSelect from "@radix-ui/react-select";
import { forwardRef, type ReactNode, type RefObject } from "react";
import { Check } from "../icons/check";
import { DownChevron } from "../icons/down-chevron";


interface DesktopThemeSelectProps {
    themes: { id: string; name: string; iconUrl?: string | null }[];
    selectedTheme: { id: string; name: string; iconUrl?: string | null } | undefined;
    onSelectTheme: (id: string) => void;
    className?: string;
}

export const DesktopThemeSelect = ({
    themes,
    selectedTheme,
    onSelectTheme,
    className,
}: DesktopThemeSelectProps) => {
    const selectedName = selectedTheme?.name ?? "Select a theme";

    return (
        <RadixSelect.Root
            value={selectedName}
            onValueChange={(name) => {
                const theme = themes.find((t) => t.name === name);
                if (theme) onSelectTheme(theme.id);
            }}
        >
            <RadixSelect.Trigger
                data-testid="theme-select-id"
                className={`flex h-[2.1875rem] items-center justify-between gap-2 whitespace-nowrap rounded-full border border-stroke bg-white px-4 py-2 text-[0.875rem] outline-none data-[placeholder]:text-primary-gray ${className}`}
            >
                <div className="flex items-center gap-2">
                    {selectedTheme?.iconUrl && (
                        <img src={selectedTheme.iconUrl} alt={selectedTheme.name} className="h-5 w-5 object-contain flex-shrink-0" />
                    )}
                    <RadixSelect.Value placeholder="Select a theme" aria-label={selectedName}>
                        {selectedName}
                    </RadixSelect.Value>
                </div>
                <RadixSelect.Icon className="text-primary-gray">
                    <DownChevron className="h-[0.875rem] w-[0.875rem]" />
                </RadixSelect.Icon>
            </RadixSelect.Trigger>

            <RadixSelect.Portal>
                <RadixSelect.Content
                    position="popper"
                    className="rounded-lg bg-white shadow-lg z-20"
                >
                    <RadixSelect.ScrollUpButton className="flex h-[1.5625rem] cursor-default items-center justify-center bg-white text-primary-gray">
                        <DownChevron className="h-[0.875rem] w-[0.875rem] rotate-180" />
                    </RadixSelect.ScrollUpButton>
                    <RadixSelect.Viewport className="max-h-[17.5rem] overflow-y-auto">
                        {themes.map((theme) => (
                            <ThemeSelectItem
                                value={theme.name}
                                key={theme.id}
                                className="cursor-pointer"
                                icon={
                                    theme.iconUrl
                                        ? <img src={theme.iconUrl} alt={theme.name} className="h-5 w-5 object-contain" />
                                        : null
                                }
                            >
                                {theme.name}
                            </ThemeSelectItem>
                        ))}
                        <RadixSelect.Separator />
                    </RadixSelect.Viewport>
                    <RadixSelect.ScrollDownButton className="flex h-[1.5625rem] cursor-default items-center justify-center bg-white text-primary-gray">
                        <DownChevron className="h-[0.875rem] w-[0.875rem]" />
                    </RadixSelect.ScrollDownButton>
                </RadixSelect.Content>
            </RadixSelect.Portal>
        </RadixSelect.Root>
    );
};

const ThemeSelectItem = forwardRef(
    (
        {
            children,
            value,
            className,
            icon,
            ...props
        }: {
            children: ReactNode;
            className?: string;
            value: string;
            icon?: JSX.Element | null;
        },
        forwardedRef
    ) => {
        return (
            <RadixSelect.Item
                className={`relative w-[18.4375rem] flex h-[2.25rem] select-none items-center rounded-lg pl-[1.5625rem] pr-[2.1875rem] text-[0.8125rem] leading-none text-primary-gray data-[disabled]:pointer-events-none data-[highlighted]:bg-stroke-secondary data-[disabled]:text-stroke data-[highlighted]:text-black data-[highlighted]:outline-none
                ${className ?? ""}`}
                value={value}
                {...props}
                ref={forwardedRef as RefObject<HTMLDivElement>}
            >
                <RadixSelect.ItemText>
                    <span className="flex items-center gap-2">
                        {icon && <span className="flex-shrink-0">{icon}</span>}
                        <span>{children}</span>
                    </span>
                </RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="absolute left-0 inline-flex w-[1.5625rem] items-center justify-center">
                    <Check className="h-[0.9375rem] w-[0.9375rem]" />
                </RadixSelect.ItemIndicator>
            </RadixSelect.Item>
        );
    }
);

ThemeSelectItem.displayName = "ThemeSelectItem";
